import type { Tool, ToolId } from "./types";

/**
 * Registry of tracked AI tools. Subscription prices are the flat monthly fees
 * the user pays; cost cards compare these against API-equivalent value.
 *
 * Colours are categorical slots validated against the dark chart surface
 * (lightness band, chroma floor, CVD separation, contrast) in the order listed
 * here — which is also the order series stack in the chart. Reordering this
 * array or substituting a hue means re-running that validation: the ordering is
 * the colourblind-safety mechanism, not decoration. Hues stay close to each
 * vendor's brand where the validated steps allow.
 *
 * The two accounts of a vendor deliberately do NOT share a hue. Two steps of one
 * hue would be an ordinal encoding — it implies rank — and near-neighbours fail
 * the CVD separation check outright. The pairing is carried by the label, which
 * is where identity belongs anyway. Last validated at 6 slots: worst adjacent
 * ΔE 9.7 (deutan), normal-vision 19.7, all contrast >= 3:1 on #151922.
 */
export const TOOLS: Tool[] = [
  {
    id: "claude-personal",
    label: "Claude Pro",
    account: "Personal",
    monthlyPriceUsd: 20,
    source: "auto",
    color: "#d95926",
  },
  {
    id: "claude-work",
    label: "Claude Business",
    account: "Work",
    // Employer-funded, so it costs *you* nothing — monthlyPriceUsd is what the
    // user pays out of pocket, which is what the spend total is comparing.
    monthlyPriceUsd: 0,
    source: "auto",
    color: "#2fa1c0",
  },
  {
    id: "copilot-personal",
    label: "Copilot",
    account: "Personal GitHub",
    // Free educational (student) plan.
    monthlyPriceUsd: 0,
    source: "auto",
    color: "#d55181",
  },
  {
    id: "copilot-work",
    label: "Copilot Business",
    account: "Work GitHub",
    monthlyPriceUsd: 0,
    source: "auto",
    color: "#9085e9",
  },
  {
    id: "codex",
    label: "Codex",
    account: "Personal (free)",
    monthlyPriceUsd: 0,
    source: "auto",
    color: "#199e70",
  },
  {
    id: "antigravity",
    label: "Antigravity",
    account: "Google AI Pro",
    // Free for 12 months on the student offer — update when that lapses.
    monthlyPriceUsd: 0,
    source: "auto",
    color: "#3987e5",
  },
];

export const TOOLS_BY_ID: Record<ToolId, Tool> = Object.fromEntries(
  TOOLS.map((t) => [t.id, t]),
) as Record<ToolId, Tool>;

export function getTool(id: ToolId): Tool {
  return TOOLS_BY_ID[id];
}
